import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { Lookup } from '../../models/lookup';

interface FlatTreeNode extends Lookup {
  level: number;
  expandable: boolean;
}

@Component({
  selector: 'app-tree-radio',
  templateUrl: './tree-radio.component.html',
  standalone: false
})
export class TreeRadioComponent implements OnInit {
  @Input() data: Lookup[] = [];
  @Input() searchText!: string;
  @Input() isSubmitted = false;
  @Input() isRequired = false;
  @Input() selectedInput: any;
  @Input() key = 'id';
  private _transformer = (node: Lookup, level: number): FlatTreeNode => ({
    ...node,
    level,
    expandable: !!node.child?.length
  });

  treeControl = new FlatTreeControl<FlatTreeNode | any>(
    node => node.level,
    node => node.expandable
  );

  treeFlattener = new MatTreeFlattener<Lookup, FlatTreeNode>(
    this._transformer,
    node => node.level,
    node => node.expandable,
    node => node.child
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  selectedNode!: Lookup | any;
  @Output() selectedItem = new EventEmitter<Lookup>();
  @Output() selectedTree = new EventEmitter<Lookup[]>();

  constructor() {
  }

  ngOnInit(): void {
    this.dataSource.data = this.data;
    // console.log(this.dataSource)
    if (this.selectedInput && typeof this.selectedInput === "number") {
      const node = this.treeControl.dataNodes.find(node => node[this.key] == this.selectedInput)!;
      this.selectNode(node);
    }
  }

  hasChild = (_: number, node: FlatTreeNode) => node.expandable;

  selectNode(node: FlatTreeNode) {
    this.selectedNode = node;
    this.selectedTree.emit(this.getParentLevelNode(node));
    this.selectedItem.emit(this.selectedNode);
  }

  isSelected(node: FlatTreeNode | any): boolean {
    return this.selectedNode && this.selectedNode[this.key] === node[this.key];
  }

  getParentLevelNode(node: FlatTreeNode): Lookup[] {
    const treeNode: any[] = [node];
    // If the node name matches the search, show it
    if (this.getLevel(node) == 0) return treeNode;
    // Traverse up to check if any ancestor (parent, grandparent, etc.) matches
    let parentNode = this.getParentNode(node);
    while (parentNode) {
      treeNode.unshift(parentNode);

      if (this.getLevel(parentNode) == 0) {
        return treeNode; // Show this node if any ancestor matches
      }
      parentNode = this.getParentNode(parentNode); // Move up the hierarchy
    }

    // If nothing matches, hide the node
    return treeNode;
  }

  /* Get the parent node of a node */
  getParentNode(node: FlatTreeNode): FlatTreeNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  public hideLeafNode(node: FlatTreeNode): boolean {
    // If the node name matches the search, show it
    if (new RegExp(this.searchText, 'i').test(node.name)) return false;

    // Traverse up to check if any ancestor (parent, grandparent, etc.) matches
    let parentNode = this.getParentNode(node);
    while (parentNode) {
      if (new RegExp(this.searchText, 'i').test(parentNode.name)) {
        return false; // Show this node if any ancestor matches
      }
      parentNode = this.getParentNode(parentNode); // Move up the hierarchy
    }

    // If nothing matches, hide the node
    return true;
  }

  // public showParentNode(node: TodoItemFlatNode): boolean {
  //   const parentNode = this.getParentNode(node);
  //   if (parentNode) {
  //     return !this.hideLeafNode(parentNode);
  //   }
  //   return true;
  // }
  public showParentNode(node: FlatTreeNode): boolean {
    if (!node) return false;

    // If node itself is not hidden, it should be visible
    if (!this.hideLeafNode(node)) return true;

    // Recursively check if any descendant matches the search
    const descendants = this.treeControl.getDescendants(node);
    return descendants.some(descendant => !this.hideLeafNode(descendant));
  }


  public hideParentNode(node: FlatTreeNode): boolean {
    return this.treeControl
      .getDescendants(node)
      .filter(node => node.child == null || node.child.length === 0)
      .every(node => this.hideLeafNode(node));
  }

  getLevel = (node: FlatTreeNode) => node.level;

  hasError() {
    return this.isRequired && this.isSubmitted && !this.selectedNode
  }
}
